import type { SortingState } from '@tanstack/react-table'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { useRef } from 'react'

export const EMPTY_BROWSE_SORTING: SortingState = []

type BrowsePageRequest = {
  filters?: unknown
  limit: number
  offset: number
  order_by: unknown
}

export type HeldBrowseDisplay = {
  entries: SystemDocumentEntry[]
  pageIndex: number
  requestKey: string
  sorting: SortingState
  totalCount: number
}

export function browsePageRequestKey(
  loadOptions: BrowsePageRequest | undefined
): string {
  if (!loadOptions) return ''
  return JSON.stringify({
    filters: loadOptions.filters ?? [],
    limit: loadOptions.limit,
    offset: loadOptions.offset,
    order_by: loadOptions.order_by,
  })
}

export function nextHeldBrowseDisplay(args: {
  browseIdentity: string
  countReady: boolean
  displayed: HeldBrowseDisplay | undefined
  hasPageError: boolean
  pageEntries: SystemDocumentEntry[] | undefined
  pageFresh: boolean
  pageIndex: number
  previousIdentity: string
  requestKey: string
  sorting: SortingState | undefined
  totalCount: number
}): {
  displayed: HeldBrowseDisplay | undefined
  identity: string
} {
  if (args.hasPageError) {
    return { displayed: undefined, identity: args.browseIdentity }
  }
  const displayed =
    args.previousIdentity === args.browseIdentity &&
    args.displayed?.requestKey === args.requestKey
      ? args.displayed
      : undefined
  if (args.pageFresh && args.countReady && args.pageEntries !== undefined) {
    return {
      displayed: {
        entries: args.pageEntries,
        pageIndex: args.pageIndex,
        requestKey: args.requestKey,
        sorting: args.sorting ?? EMPTY_BROWSE_SORTING,
        totalCount: args.totalCount,
      },
      identity: args.browseIdentity,
    }
  }
  return { displayed, identity: args.browseIdentity }
}

export function useHeldBrowseDisplay(args: {
  browseIdentity: string
  countReady: boolean
  hasPageError: boolean
  pageEntries: SystemDocumentEntry[] | undefined
  pageFresh: boolean
  pageIndex: number
  requestKey: string
  sorting: SortingState | undefined
  totalCount: number
}): HeldBrowseDisplay | undefined {
  const identityRef = useRef(args.browseIdentity)
  const displayedRef = useRef<HeldBrowseDisplay | undefined>(undefined)
  const next = nextHeldBrowseDisplay({
    ...args,
    displayed: displayedRef.current,
    previousIdentity: identityRef.current,
  })
  identityRef.current = next.identity
  displayedRef.current = next.displayed
  return next.displayed
}
